import { useEffect, useRef, useState, type FormEvent } from 'react';
import Player from './components/Player';
import { fetchTracks, fetchPhotos, fetchLiveEvents, type Track, type Photo, type LiveEvent } from './lib/api';

const sections = [
  { id: 'music', label: 'MUSIC' },
  { id: 'live', label: 'LIVE' },
  { id: 'photo', label: 'PHOTO' },
];

function App() {
  const [hideNavLinks, setHideNavLinks] = useState(false);
  const [navVisible, setNavVisible] = useState(false);
  const [hideNavBar, setHideNavBar] = useState(false);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [tracksLoading, setTracksLoading] = useState(true);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [photosLoading, setPhotosLoading] = useState(true);
  const [liveEvents, setLiveEvents] = useState<LiveEvent[]>([]);
  const [liveLoading, setLiveLoading] = useState(true);
  const [lightbox, setLightbox] = useState<Photo | null>(null);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [landscapePhotos, setLandscapePhotos] = useState<Record<number, boolean>>({});
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactStatus, setContactStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [contactError, setContactError] = useState('');

  const heroImageDesktop = '/media/hero/cover.jpg';
  const heroImageMobile = '/media/hero/cover-mobile.jpg';
  const heroImageTabletPortrait = '/media/hero/cover_tablet_verticale.jpg';
  const heroImageTabletLandscape = '/media/hero/cover-tablet-orrizontale.jpg';

  useEffect(() => {
    const timer = setTimeout(() => setNavVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await fetchTracks();
        if (active) setTracks(data);
      } catch (err) {
        if (active) setTracks([]);
      } finally {
        if (active) setTracksLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!photos.length) return undefined;
    const el = carouselRef.current;
    if (!el) return undefined;

    const onScroll = () => {
      const slide = el.querySelector<HTMLElement>('.photo-slide');
      if (!slide) return;
      const w = slide.getBoundingClientRect().width || 1;
      const nextIdx = Math.round(el.scrollLeft / w);
      setCarouselIndex(nextIdx);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [photos]);

  useEffect(() => {
    if (!photos.length) return undefined;
    const isMobile = window.matchMedia('(max-width: 640px)').matches;
    if (!isMobile) return undefined;
    const el = carouselRef.current;
    if (!el) return undefined;
    const slides = Array.from(el.querySelectorAll<HTMLElement>('.photo-slide'));
    if (!slides.length) return undefined;

    let idx = carouselIndex;
    const tick = () => {
      idx = (idx + 1) % slides.length;
      setCarouselIndex(idx);
      const slideWidth = slides[0].getBoundingClientRect().width;
      el.scrollTo({ left: slideWidth * idx, behavior: 'smooth' });
    };

    const timer = window.setInterval(tick, 2600);
    return () => window.clearInterval(timer);
  }, [photos, carouselIndex]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await fetchLiveEvents();
        if (active) setLiveEvents(data);
      } catch (err) {
        if (active) setLiveEvents([]);
      } finally {
        if (active) setLiveLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await fetchPhotos();
        if (active) setPhotos(data);
      } catch (err) {
        if (active) setPhotos([]);
      } finally {
        if (active) setPhotosLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setHideNavLinks(y > 20);
      setHideNavBar(y > 60);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  };

  const handleContactSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactError('');
    const name = contactForm.name.trim();
    const email = contactForm.email.trim();
    const message = contactForm.message.trim();
    if (!name || !email || !message) {
      setContactError('Compila tutti i campi.');
      return;
    }
    setContactStatus('sending');
    try {
      const res = await fetch('/api/contact/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message }),
      });
      if (!res.ok) {
        throw new Error('Invio fallito');
      }
      setContactStatus('success');
      setContactForm({ name: '', email: '', message: '' });
    } catch (err) {
      setContactStatus('error');
      setContactError('Invio non riuscito, riprova.');
    }
  };

  return (
    <div className="page">
      <header className={`navbar nav-clear ${navVisible ? 'nav-visible' : 'nav-hidden'} ${hideNavBar ? 'navbar-hide' : ''}`}>
        <div className="container navbar-inner">
          <div className="nav-spacer" />
          <img className={`nav-logo ${hideNavLinks ? 'nav-logo-hide' : ''}`} src="/static/sito/img/logo4.png" alt="Gang Band" />
          <div className="nav-right">
            <nav className={`nav-links ${hideNavLinks ? 'nav-links-hide' : ''}`}>
              {sections.map((link) => (
                <button key={link.id} type="button" className="nav-link" onClick={() => scrollToId(link.id)}>
                  {link.label}
                </button>
              ))}
            </nav>
            <div className="nav-social">
              <a className="icon-btn instagram" href="https://www.instagram.com/gangbandts?igsh=MWgwaG83MzVrYzY4OA==" target="_blank" rel="noreferrer" aria-label="Instagram">
                <img src="/media/hero/icons8-instagram-100.png" alt="Instagram" />
              </a>
              <a className="icon-btn facebook" href="https://www.facebook.com/gang.band.trieste" target="_blank" rel="noreferrer" aria-label="Facebook">
                <img src="/media/hero/icons8-facebook-nuovo-80.png" alt="Facebook" />
              </a>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section
          className="hero-full"
          style={{
            ['--hero-desktop' as any]: `url(${heroImageDesktop})`,
            ['--hero-mobile' as any]: `url(${heroImageMobile})`,
            ['--hero-tablet-portrait' as any]: `url(${heroImageTabletPortrait})`,
            ['--hero-tablet-landscape' as any]: `url(${heroImageTabletLandscape})`,
          }}
        />

        <section id="music" className="section music-section">
          <div className="container music-wrap">
            {!tracksLoading && tracks.length > 0 && <Player tracks={tracks} />}
          </div>
        </section>

        <section id="live" className="section live-section">
          <div className="container live-wrap">
            <div className="section-head">
              <h2>LIVE</h2>
            </div>
            {liveLoading && <p className="muted">Caricamento...</p>}
            {!liveLoading && liveEvents.length === 0 && <p className="muted">Nessuna data disponibile.</p>}
            {!liveLoading && liveEvents.length > 0 && (
              <div className="live-list">
                {liveEvents.map((event) => {
                  const parsedDate = event.date ? new Date(event.date) : null;
                  const validDate = parsedDate && !Number.isNaN(parsedDate.getTime());
                  const isPast = !!(validDate && parsedDate < new Date());
                  const dateLabel = validDate
                    ? parsedDate.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
                    : event.date_label || 'Coming soon';
                  const weekday = validDate ? parsedDate.toLocaleDateString('it-IT', { weekday: 'short' }) : '';
                  const statusCopy: Record<string, string> = {
                    on_sale: 'On sale',
                    free: 'Free entry',
                    sold_out: 'Sold out',
                    last_tickets: 'Ultimi posti',
                  };
                  const statusText = statusCopy[event.status] ?? '';
                  return (
                    <div key={event.id} className={`live-row${isPast ? ' live-row--past' : ''}`}>
                      <div className={`live-date ${validDate ? '' : 'live-date-open'}`}>
                        <span className="weekday">{weekday || 'SOON'}</span>
                        <span className="day">{dateLabel}</span>
                      </div>
                      <div className="live-meta">
                        <div className="live-venue">{event.venue}</div>
                        <div className="live-city">{event.city}</div>
                        {statusText && <span className={`status-badge status-${event.status}`}>{statusText}</span>}
                        {isPast && <span className="status-badge status-past">già stata</span>}
                        {event.notes ? <div className="live-notes">{event.notes}</div> : null}
                      </div>
                      <div className="live-actions">
                        {event.ticket_url && !isPast ? (
                          <a className="live-ticket" href={event.ticket_url} target="_blank" rel="noreferrer">
                            Tickets
                          </a>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section id="photo" className="section photo-section">
          <div className="container">
            {!photosLoading && photos.length > 0 && (
              <>
                <div className="photo-grid desktop-grid">
                  {photos.map((p) => (
                    <figure key={p.id} className={`photo-card ${landscapePhotos[p.id] ? 'landscape' : 'portrait'}`}>
                      <button type="button" className="photo-btn" onClick={() => setLightbox(p)} aria-label="Apri foto">
                        <img
                          src={p.image}
                          alt=""
                          loading="lazy"
                          onLoad={(e) => {
                            const img = e.currentTarget;
                            if (img.naturalWidth > img.naturalHeight) {
                              setLandscapePhotos((prev) => ({ ...prev, [p.id]: true }));
                            }
                          }}
                        />
                      </button>
                    </figure>
                  ))}
                </div>

                <div className="photo-carousel mobile-carousel" ref={carouselRef}>
                  {photos.map((p, i) => (
                    <button
                      key={p.id}
                      type="button"
                      className={`photo-slide ${i === carouselIndex ? 'is-active' : ''}`}
                      onClick={() => setLightbox(p)}
                      aria-label="Apri foto"
                    >
                      <img src={p.image} alt="" loading="lazy" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        <section className="section contact-section">
          <div className="container contact-grid">
            <div className="contact-card">
              <h3 className="contact-title">Contattaci</h3>
              <form className="contact-form" onSubmit={handleContactSubmit}>
                <div className="contact-field">
                  <label className="contact-label" htmlFor="contact-name">Nome</label>
                  <input
                    id="contact-name"
                    className="contact-input"
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => setContactForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="contact-field">
                  <label className="contact-label" htmlFor="contact-email">Email</label>
                  <input
                    id="contact-email"
                    className="contact-input"
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm((prev) => ({ ...prev, email: e.target.value }))}
                    required
                  />
                </div>
                <div className="contact-field contact-field--wide">
                  <label className="contact-label" htmlFor="contact-message">Messaggio</label>
                  <textarea
                    id="contact-message"
                    className="contact-textarea"
                    rows={4}
                    value={contactForm.message}
                    onChange={(e) => setContactForm((prev) => ({ ...prev, message: e.target.value }))}
                    required
                  />
                </div>
                <div className="contact-field contact-field--wide contact-actions">
                  <button className="contact-btn" type="submit" disabled={contactStatus === 'sending'}>
                    {contactStatus === 'sending' ? 'Invio...' : 'Invia'}
                  </button>
                  {contactStatus === 'success' && <div className="contact-status">Messaggio inviato, grazie!</div>}
                  {(contactStatus === 'error' || contactError) && (
                    <div className="contact-status">{contactError || 'Invio non riuscito, riprova.'}</div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>

        {lightbox && (
          <div className="lightbox" role="button" tabIndex={-1} onClick={() => setLightbox(null)}>
            <div className="lightbox-inner" onClick={(e) => e.stopPropagation()}>
              <img src={lightbox.image} alt="" />
            </div>
          </div>
        )}

      </main>

      <footer className="footer-bar">
        <div className="container navbar-inner">
          <div className="nav-spacer" />
          <img className="nav-logo" src="/static/sito/img/logo4.png" alt="Gang Band" />
          <div className="nav-right">
            <nav className="nav-links">
              {sections.map((link) => (
                <button key={link.id} type="button" className="nav-link" onClick={() => scrollToId(link.id)}>
                  {link.label}
                </button>
              ))}
            </nav>
            <div className="nav-social">
              <a className="icon-btn instagram" href="https://www.instagram.com/gangbandts?igsh=MWgwaG83MzVrYzY4OA==" target="_blank" rel="noreferrer" aria-label="Instagram">
                <img src="/media/hero/icons8-instagram-100.png" alt="Instagram" />
              </a>
              <a className="icon-btn facebook" href="https://www.facebook.com/gang.band.trieste" target="_blank" rel="noreferrer" aria-label="Facebook">
                <img src="/media/hero/icons8-facebook-nuovo-80.png" alt="Facebook" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
